
/*
  Women Spot Smart Cart
  Keeps Shopify's native quantity and remove controls.
*/

(() => {
  "use strict";

  if (window.__womenSpotSmartCartV5) return;
  window.__womenSpotSmartCartV5 = true;

  const configNode = document.querySelector(
    "[data-ws-cart-config]"
  );

  if (!configNode) return;

  const root = (
    window.Shopify?.routes?.root || "/"
  ).replace(/\/?$/, "/");

  const path = value =>
    root + value.replace(/^\//, "");

  const threshold =
    Number(configNode.dataset.threshold) || 300000;

  const offerAmount =
    Number(configNode.dataset.discount) || 55500;

  const collection =
    configNode.dataset.collection || "all";

  const currency =
    configNode.dataset.currency || "INR";

  const money = cents =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency,
      maximumFractionDigits: 2
    }).format((Number(cents) || 0) / 100);

  const escapeHTML = value =>
    String(value ?? "").replace(
      /[&<>"']/g,
      char => ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;"
      })[char]
    );

  // Shopify products.json prices are major-unit strings.
  const productCents = value =>
    Math.round(Number(value || 0) * 100);

  let cart = null;
  let products = [];
  let updateTimer = null;
  let fetching = false;
  let adding = false;
  let mutationObserver = null;
  let lastNativeSignature = "";

  async function getJSON(endpoint, options) {
    const response = await fetch(path(endpoint), {
      credentials: "same-origin",
      cache: "no-store",
      ...options
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data.description ||
        data.message ||
        `Shopify returned ${response.status}`
      );
    }

    return data;
  }

  function drawer() {
    return document.querySelector("cart-drawer");
  }

  function nativeSignature() {
    const el = drawer();

    if (!el) return "";

    return [...el.querySelectorAll(".cart-item")]
      .map(row => {
        const input = row.querySelector(
          ".quantity__input"
        );

        return [
          row.id,
          input?.value,
          row.querySelector(
            ".cart-item__name"
          )?.textContent?.trim()
        ].join(":");
      })
      .join("|") +
      "|" +
      Boolean(el.classList.contains("is-empty"));
  }

  /* Diwali progress */

  function offerHTML() {
    const subtotal = Number(
      cart?.original_total_price || 0
    );

    const percent = Math.min(
      100,
      Math.max(
        0,
        (subtotal / threshold) * 100
      )
    );

    const remaining = Math.max(
      0,
      threshold - subtotal
    );

    const reached = subtotal >= threshold;

    return `
      <div class="ws-offer-top">
        <strong>🎁 Diwali Special Offer</strong>

        <span class="ws-offer-pill">
          ${money(offerAmount)} OFF
        </span>
      </div>

      ${
        reached
          ? `
            <div class="ws-offer-success">
              🎉 Congratulations!
              You reached the offer target.
              Your discount is subject to
              Shopify eligibility rules.
            </div>
          `
          : `
            <p class="ws-offer-message">
              Add <strong>${money(remaining)}</strong>
              more to reach the offer target!
            </p>
          `
      }

      <div
        class="ws-progress"
        role="progressbar"
        aria-label="Diwali offer progress"
        aria-valuemin="0"
        aria-valuemax="100"
        aria-valuenow="${Math.round(percent)}"
      >
        <span style="width:${percent}%"></span>
      </div>

      <div class="ws-progress-labels">
        <span>${money(subtotal)}</span>
        <span>${money(threshold)} target</span>
      </div>
    `;
  }

  /* Find actual Shopify product and variant */

  function findProduct(item) {
    return products.find(
      product =>
        String(product.id) ===
        String(item.product_id)
    );
  }

  function findVariant(item) {
    return findProduct(item)?.variants?.find(
      variant =>
        String(variant.id) ===
        String(item.variant_id)
    );
  }

  /* Added-product prices */

  function linePrices(item) {
    const qty = Number(item.quantity || 1);

    const final = Number(
      item.final_line_price ??
      item.line_price ??
      0
    );

    const original = Number(
      item.original_line_price ?? final
    );

    const compare =
      productCents(
        findVariant(item)?.compare_at_price
      ) * qty;

    const baseline = Math.max(
      original,
      compare,
      final
    );

    const saved = Math.max(
      0,
      baseline - final
    );

    const pct = baseline > final
      ? Math.round(
          (saved / baseline) * 100
        )
      : 0;

    return `
      <div class="ws-line-prices">
        ${
          saved
            ? `
              <s class="ws-line-old">
                ${money(baseline)}
              </s>
            `
            : ""
        }

        <strong class="ws-line-new">
          ${money(final)}
        </strong>

        ${
          pct
            ? `
              <span class="ws-line-off">
                ${pct}% OFF
              </span>
            `
            : ""
        }
      </div>
    `;
  }

  function decorateRows() {
    const rows =
      drawer()?.querySelectorAll(".cart-item") || [];

    rows.forEach(row => {
      const index = Number(
        row.id.match(
          /CartDrawer-Item-(\d+)/
        )?.[1] || 0
      ) - 1;

      const item = cart?.items?.[index];

      const details = row.querySelector(
        ".cart-item__details"
      );

      if (!item || !details) return;

      let badge = details.querySelector(
        ".ws-line-prices"
      );

      if (!badge) {
        badge = document.createElement("div");
        badge.className = "ws-line-prices";

        const title = details.querySelector(
          ".cart-item__title"
        );

        if (title) {
          title.insertAdjacentElement(
            "afterend",
            badge
          );
        } else {
          details.appendChild(badge);
        }
      }

      const tmp = document.createElement("div");
      tmp.innerHTML = linePrices(item);

      badge.innerHTML =
        tmp.firstElementChild?.innerHTML || "";

      details.classList.add(
        "ws-has-line-price"
      );
    });
  }

  /* Recommended products */

  function productCard(product) {
    const variants = (
      product.variants || []
    ).filter(variant => variant.available);

    if (!variants.length) return "";

    const image = product.images?.[0];

    const imageUrl =
      typeof image === "string"
        ? image
        : image?.src;

    const href = path(
      "products/" +
      encodeURIComponent(product.handle)
    );

    const options = variants.map(variant => {
      const title =
        variant.title === "Default Title"
          ? "Standard"
          : variant.title;

      return `
        <option value="${escapeHTML(variant.id)}">
          ${escapeHTML(title)}
        </option>
      `;
    }).join("");

    return `
      <article
        class="ws-product"
        data-product-id="${escapeHTML(product.id)}"
      >
        <a href="${escapeHTML(href)}">
          ${
            imageUrl
              ? `
                <img
                  src="${escapeHTML(imageUrl)}"
                  alt="${escapeHTML(product.title)}"
                  loading="lazy"
                >
              `
              : ""
          }

          ${escapeHTML(product.title)}
        </a>

        <div class="ws-product-price"></div>

        <div class="ws-product-saving"></div>

        <select
          aria-label="Available variants for ${escapeHTML(product.title)}"
        >
          ${options}
        </select>

        <button
          type="button"
          class="ws-add"
        >
          + Add to Cart
        </button>

        <div
          class="ws-product-error"
          role="alert"
          hidden
        ></div>
      </article>
    `;
  }

  function updateCardPrice(card) {
    const product = products.find(
      item =>
        String(item.id) ===
        card.dataset.productId
    );

    const variant = product?.variants?.find(
      item =>
        String(item.id) ===
        card.querySelector("select")?.value
    );

    if (!variant) return;

    const price = productCents(
      variant.price
    );

    const compare = productCents(
      variant.compare_at_price
    );

    const saved = Math.max(
      0,
      compare - price
    );

    const off = compare > price
      ? Math.round(
          (saved / compare) * 100
        )
      : 0;

    card.querySelector(
      ".ws-product-price"
    ).innerHTML = `
      ${
        saved
          ? `<s>${money(compare)}</s>`
          : ""
      }

      <strong>${money(price)}</strong>

      ${
        off
          ? `
            <span
              style="color:#16733c;font-size:10px"
            >
              ${off}% OFF
            </span>
          `
          : ""
      }
    `;

    card.querySelector(
      ".ws-product-saving"
    ).textContent = saved
      ? `You save ${money(saved)}`
      : "";
  }

  function renderRecommendations() {
    const panel = drawer()?.querySelector(
      ".ws-recommendations"
    );

    if (!panel) return;

    const existing = panel.querySelector(
      ".ws-products"
    );

    const scroll = existing?.scrollLeft || 0;

    const inCart = new Set(
      (cart?.items || []).map(
        item => String(item.product_id)
      )
    );

    const shown = products
      .filter(
        product =>
          !inCart.has(String(product.id)) &&
          product.variants?.some(
            variant => variant.available
          )
      )
      .slice(0, 14);

    panel.innerHTML = `
      <h3>💗 You May Also Like</h3>

      ${
        shown.length
          ? `
            <div class="ws-products">
              ${shown.map(productCard).join("")}
            </div>
          `
          : `
            <p style="font-size:12px">
              Choose a collection with published,
              available products in Smart Cart settings.
            </p>
          `
      }
    `;

    panel.querySelectorAll(
      ".ws-product"
    ).forEach(updateCardPrice);

    const slider = panel.querySelector(
      ".ws-products"
    );

    if (slider) {
      slider.scrollLeft = scroll;
    }
  }

  /* Actual savings */

  function renderSavings() {
    const panel = drawer()?.querySelector(
      ".ws-savings"
    );

    if (!panel) return;

    const compareBaseline = (
      cart?.items || []
    ).reduce((sum, item) => {
      const compare =
        productCents(
          findVariant(item)?.compare_at_price
        ) * Number(item.quantity || 0);

      return sum + Math.max(
        compare,
        Number(item.original_line_price || 0)
      );
    }, 0);

    const saved = Math.max(
      0,
      compareBaseline -
      Number(cart?.total_price || 0)
    );

    panel.hidden = saved === 0;

    panel.innerHTML = saved
      ? `
        <span>🏷️ You Saved</span>
        <strong>${money(saved)}</strong>
      `
      : "";
  }

  /* Insert custom panels around native Shopify cart */

  function ensurePanels() {
    const el = drawer();

    if (!el) return;

    const cartItems = el.querySelector(
      "cart-drawer-items"
    );

    if (
      cartItems &&
      !cartItems.querySelector(".ws-offer")
    ) {
      const offer = document.createElement(
        "section"
      );

      offer.className = "ws-offer";

      cartItems.insertBefore(
        offer,
        cartItems.firstChild
      );
    }

    if (
      cartItems &&
      !cartItems.querySelector(
        ".ws-recommendations"
      )
    ) {
      const rec = document.createElement(
        "section"
      );

      rec.className = "ws-recommendations";

      cartItems.appendChild(rec);
    }

    const footer = el.querySelector(
      ".drawer__footer"
    );

    if (
      footer &&
      !footer.querySelector(".ws-savings")
    ) {
      const savings = document.createElement(
        "div"
      );

      savings.className = "ws-savings";

      const summary = footer.querySelector(
        ".cart-drawer__footer"
      );

      footer.insertBefore(
        savings,
        summary || footer.firstChild
      );
    }
  }

  function render() {
    if (!cart || adding) return;

    const el = drawer();

    if (!el) return;

    ensurePanels();

    const offer = el.querySelector(
      ".ws-offer"
    );

    if (offer) {
      offer.hidden = cart.item_count === 0;
      offer.innerHTML = offerHTML();
    }

    decorateRows();
    renderRecommendations();
    renderSavings();
  }

  /* Update data when Shopify cart changes */

  async function refresh() {
    if (fetching || adding) return;

    fetching = true;

    try {
      cart = await getJSON("cart.js");
      render();
    } catch (error) {
      console.warn(
        "Women Spot cart:",
        error
      );
    } finally {
      fetching = false;
    }
  }

  function schedule(delay = 450) {
    clearTimeout(updateTimer);

    updateTimer = setTimeout(
      refresh,
      delay
    );
  }

  function watch() {
    const el = drawer();

    if (!el || mutationObserver) return;

    lastNativeSignature = nativeSignature();

    mutationObserver = new MutationObserver(() => {
      const signature = nativeSignature();

      if (signature !== lastNativeSignature) {
        lastNativeSignature = signature;
        schedule(300);
      }

      if (
        !el.querySelector(".ws-recommendations") ||
        !el.querySelector(".ws-offer")
      ) {
        schedule(300);
      }
    });

    mutationObserver.observe(el, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: [
        "class",
        "value"
      ]
    });
  }

  /* Add selected recommended variant */

  async function addRecommendation(button) {
    if (adding) return;

    const card = button.closest(
      ".ws-product"
    );

    const variantId = Number(
      card?.querySelector("select")?.value
    );

    if (!variantId) return;

    const error = card.querySelector(
      ".ws-product-error"
    );

    error.hidden = true;
    error.textContent = "";

    button.disabled = true;
    button.textContent = "Adding...";

    adding = true;

    let added = false;

    try {
      const el = drawer();

      const sectionIds =
        typeof el?.getSectionsToRender === "function"
          ? el.getSectionsToRender().map(
              section => section.id
            )
          : [
              "cart-drawer",
              "cart-icon-bubble"
            ];

      const result = await getJSON(
        "cart/add.js",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json"
          },
          body: JSON.stringify({
            items: [{
              id: variantId,
              quantity: 1
            }],
            sections: sectionIds,
            sections_url:
              window.location.pathname
          })
        }
      );

      added = true;

      if (
        typeof el?.renderContents === "function" &&
        result.sections?.["cart-drawer"]
      ) {
        el.renderContents(result);
      } else {
        // Safer than replacing native drawer HTML.
        window.location.reload();
        return;
      }

    } catch (err) {
      error.hidden = false;

      error.textContent = added
        ? "Added. Reopen the cart if it does not update."
        : err.message;

      button.disabled = false;
      button.textContent = "+ Add to Cart";

    } finally {
      adding = false;
      schedule(350);
    }
  }

  /* Button events */

  document.addEventListener(
    "click",
    event => {
      const add = event.target.closest?.(
        "cart-drawer .ws-add"
      );

      if (add) {
        event.preventDefault();
        addRecommendation(add);
        return;
      }

      if (
        event.target.closest?.(
          "cart-drawer .quantity__button, " +
          "cart-drawer cart-remove-button"
        )
      ) {
        schedule(900);
      }
    }
  );

  /* Size and quantity changes */

  document.addEventListener(
    "change",
    event => {
      const select = event.target.closest?.(
        "cart-drawer .ws-product select"
      );

      if (select) {
        updateCardPrice(
          select.closest(".ws-product")
        );
        return;
      }

      if (
        event.target.matches?.(
          "cart-drawer .quantity__input"
        )
      ) {
        schedule(900);
      }
    }
  );

  document.addEventListener(
    "shopify:section:load",
    () => schedule(300)
  );

  document.addEventListener(
    "visibilitychange",
    () => {
      if (!document.hidden) {
        schedule(150);
      }
    }
  );

  /* Initialize */

  async function init() {
    watch();

    await refresh();

    try {
      const result = await getJSON(
        "collections/" +
        encodeURIComponent(collection) +
        "/products.json?limit=50"
      );

      products = result.products || [];

      render();

    } catch (error) {
      console.warn(
        "Women Spot recommendations:",
        error
      );
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      init
    );
  } else {
    init();
  }
})();
